(() => {
  const GRID_SELECTOR = '[data-yoel-artwork-grid]';
  const CARD_SELECTOR = ':scope > [data-artist-store-order]';

  const numericOrder = (card) => {
    const order = Number(card.dataset.artistStoreOrder);
    return Number.isFinite(order) ? order : 0;
  };

  const findMatchingGrid = (documentFragment, sectionId) =>
    Array.from(documentFragment.querySelectorAll(GRID_SELECTOR)).find(
      (candidate) => candidate.dataset.sectionId === sectionId
    );

  const sortGrid = (grid) => {
    const cards = Array.from(grid.querySelectorAll(CARD_SELECTOR));
    cards.sort((firstCard, secondCard) => numericOrder(secondCard) - numericOrder(firstCard));

    const fragment = document.createDocumentFragment();
    cards.forEach((card) => fragment.append(card));
    grid.append(fragment);
  };

  const layoutMasonry = (grid) => {
    const cards = Array.from(grid.querySelectorAll(CARD_SELECTOR));
    if (cards.length === 0 || grid.clientWidth === 0) return;

    // Until this flag is enabled, the shared grid snippet uses regular auto
    // rows. This prevents unloaded cards from overlapping while additional
    // collection pages are fetched and sorted.
    grid.dataset.masonryReady = 'true';

    cards.forEach((card) => {
      card.style.removeProperty('grid-column-start');
      card.style.removeProperty('grid-row-start');
      card.style.removeProperty('grid-row-end');
    });

    const styles = window.getComputedStyle(grid);
    const columns = Math.max(1, styles.gridTemplateColumns.split(' ').length);
    const rowHeight = Number.parseFloat(styles.gridAutoRows) || 1;
    const rowGap = Number.parseFloat(styles.rowGap) || 0;
    const nextAvailableRow = Array(columns).fill(1);

    cards.forEach((card) => {
      const shortestColumn = nextAvailableRow.indexOf(Math.min(...nextAvailableRow));
      const cardHeight = card.getBoundingClientRect().height;
      const rowSpan = Math.max(1, Math.ceil((cardHeight + rowGap) / (rowHeight + rowGap)));

      card.style.gridColumnStart = String(shortestColumn + 1);
      card.style.gridRowStart = String(nextAvailableRow[shortestColumn]);
      card.style.gridRowEnd = `span ${rowSpan}`;

      nextAvailableRow[shortestColumn] += rowSpan;
    });
  };

  const initializeMasonry = (grid) => {
    let animationFrame;
    const scheduleLayout = () => {
      window.cancelAnimationFrame(animationFrame);
      animationFrame = window.requestAnimationFrame(() => layoutMasonry(grid));
    };

    grid.querySelectorAll('img').forEach((image) => {
      if (!image.complete) {
        image.addEventListener('load', scheduleLayout, { once: true });
        image.addEventListener('error', scheduleLayout, { once: true });
      }
    });

    window.addEventListener('resize', scheduleLayout, { passive: true });
    document.fonts?.ready.then(scheduleLayout);
    scheduleLayout();
  };

  const loadAndSortCompleteCollection = async (grid) => {
    if (grid.dataset.orderingInitialized === 'true') return;

    grid.dataset.orderingInitialized = 'true';

    if (Number(grid.dataset.currentPage) !== 1) {
      sortGrid(grid);
      grid.setAttribute('aria-busy', 'false');
      initializeMasonry(grid);
      return;
    }

    let nextPageUrl = grid.dataset.nextPageUrl;
    const sectionId = grid.dataset.sectionId;
    const visitedUrls = new Set();

    grid.setAttribute('aria-busy', 'true');

    try {
      while (nextPageUrl) {
        const absoluteUrl = new URL(nextPageUrl, window.location.origin);

        if (absoluteUrl.origin !== window.location.origin || visitedUrls.has(absoluteUrl.href)) break;

        visitedUrls.add(absoluteUrl.href);
        absoluteUrl.searchParams.set('section_id', sectionId);

        const response = await fetch(absoluteUrl.href, {
          credentials: 'same-origin',
          headers: { Accept: 'text/html' },
        });

        if (!response.ok) throw new Error(`Unable to load artwork page (${response.status})`);

        const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
        const nextGrid = findMatchingGrid(documentFragment, sectionId);

        if (!nextGrid) throw new Error('Unable to find the artwork grid in the next page');

        const fragment = document.createDocumentFragment();
        Array.from(nextGrid.querySelectorAll(CARD_SELECTOR)).forEach((card) => fragment.append(card));
        grid.append(fragment);

        nextPageUrl = nextGrid.dataset.nextPageUrl;
      }

      sortGrid(grid);
    } catch (error) {
      console.error('[Yoel artwork grid]', error);
      sortGrid(grid);
    } finally {
      grid.setAttribute('aria-busy', 'false');
      initializeMasonry(grid);
    }
  };

  const initializeArtworkGrids = (root = document) => {
    root.querySelectorAll(GRID_SELECTOR).forEach((grid) => loadAndSortCompleteCollection(grid));
  };

  initializeArtworkGrids();
  document.addEventListener('shopify:section:load', (event) => initializeArtworkGrids(event.target));
})();
